/** @jsxImportSource @emotion/react */

import { t as coreT } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import { format } from 'date-fns';

import { AdminUserWithBalance } from 'modules/organizers/domain/interfaces/admin-user';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { baseApi } from 'shared/api/base-api';
import { AppDispatch } from 'store/store';
import { useDispatchWithToast } from 'store/use-dispatch-with-toast';
import Modal from 'ud-ui/components/modal';
import UDButton from 'ud-ui/components/button';
import UDText from 'ud-ui/components/text';
import { Plan } from 'modules/organizers/domain/interfaces/plan';
import { useGetActiveTournamentPlanQuery } from 'modules/organizer/store/organizer-plan-api';
import { givePlan } from 'modules/organizers/store/actions';
import { selectPlansList } from 'modules/organizers/store/selectors';
import PlanCard from 'modules/organizers/ui/components/plan-card';
import { disableButtonCSS, MainBlock, PlansList } from './styles';
import { theme } from 'styles/theme/theme';

interface IProps {
  tournamentId: number;
  owner?: AdminUserWithBalance;
}

function PlansBlock(props: IProps) {
  const { t } = useLingui();
  const dispatch = useDispatch<AppDispatch>();
  const dispatchWithToast = useDispatchWithToast();
  const { tournamentId, owner } = props;

  const plans = useSelector(selectPlansList);
  const [selectedPlan, setSelectedPlan] = useState<Plan>();
  const [tariff, setTariff] = useState<string>();
  const { data: activePlan, isError: isActivePlanError } = useGetActiveTournamentPlanQuery({ tournamentId });
  // `undefined` — тариф ещё не загружен, `null` — тарифа нет, он «Бесплатный» (id 0).
  const currentPlanId = activePlan === undefined ? undefined : (activePlan?.id ?? 0);
  const balance = useMemo(() => {
    return owner?.balance ? Number((owner?.balance / 100).toFixed(2)) : 0;
  }, [owner]);

  const [isOpen, setIsOpen] = useState(false);
  const open = useCallback(() => {
    setIsOpen(true);
  }, []);
  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  useEffect(() => {
    if (isActivePlanError) {
      // core-`t`, а не из хука: `t` в зависимостях повторял бы тост при смене языка.
      toast.error(coreT`Не удалось получить активный план`);
    }
  }, [isActivePlanError]);

  const closeModal = useCallback(() => {
    setTariff(undefined);
    setSelectedPlan(undefined);
    close();
  }, [close]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeModal();
      }
    };

    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [closeModal]);

  const handleGivePlan = useCallback(() => {
    if (owner === undefined) {
      toast.error(t`Пользователь не найден`);
      return;
    }

    const planId = selectedPlan?.id;
    if (planId === undefined) {
      toast.error(t`Не удалось определить план`);
      return;
    }

    dispatchWithToast(
      givePlan({
        tournamentId: tournamentId,
        recipientId: owner.id,
        tournamentPlanId: planId,
      }),
      { success: t`План подключен`, error: t`Не удалось выдать план` },
    ).then((result) => {
      if (!result) return;

      // Тег 'Organizer' помечает и баланс владельца, и активный тариф —
      // обе выборки перечитаются сами, отдельный запрос за планом не нужен.
      dispatch(baseApi.util.invalidateTags([{ type: 'Organizer', id: tournamentId }]));
      closeModal();
    });
  }, [owner, selectedPlan?.id, dispatch, dispatchWithToast, tournamentId, closeModal, t]);

  const isCanBuy = Boolean(selectedPlan?.price && selectedPlan?.price <= balance);

  const currentPlan = plans.find((plan) => plan.id === currentPlanId);
  const currentPlanName = currentPlan ? t(currentPlan.name) : undefined;
  const selectedPlanName = selectedPlan ? t(selectedPlan.name) : '';
  const selectedPlanPrice = selectedPlan?.price;
  const shortage = (selectedPlan?.price ?? 0) - balance;
  const activeToLabel = activePlan?.subscription?.activeTo
    ? format(new Date(activePlan.subscription.activeTo), 'dd.MM.yy')
    : t({ message: 'Неизвестно', comment: 'Дата окончания подписки неизвестна' });

  return (
    <MainBlock>
      <div className={'mb-7'}>
        <UDText type={'subhead'}>
          <Trans>Данный пользователь использует план:</Trans>{' '}
          <span style={{ color: theme.colors.AccentNaplesYellow, fontWeight: 'bold' }}>{currentPlanName}</span>
        </UDText>
      </div>
      <PlansList>
        {plans.map((plan, index) => {
          const planLength = plans.length;
          const isActive = plan.id === currentPlanId;
          const activeFrom = activePlan?.subscription?.activeFrom
            ? format(new Date(activePlan.subscription.activeFrom), 'dd.MM.yy')
            : undefined;
          const activeTo = activePlan?.subscription?.activeTo
            ? format(new Date(activePlan.subscription.activeTo), 'dd.MM.yy')
            : undefined;

          const onClick = () => {
            if (plan.id < 0) return;
            open();
            setSelectedPlan(plan);
          };

          return (
            <PlanCard
              isDisabled={plan.id < 0}
              onClick={onClick}
              key={plan.id}
              name={t(plan.name)}
              price={plan.price}
              isActive={isActive}
              compact
              date={
                isActive
                  ? {
                      from: activeFrom,
                      to: activeTo,
                    }
                  : undefined
              }
              widthPercent={100 - (planLength - (index + 1)) * 5}
            />
          );
        })}
      </PlansList>

      <Modal show={isOpen} onClose={closeModal} size="md">
        {selectedPlan && selectedPlan?.id === activePlan?.id ? (
          <div className={'flex flex-col items-start'}>
            <UDText type={'title-3'} style={{ marginBottom: 30 }}>
              <Trans>{selectedPlanName} план</Trans>
            </UDText>
            <UDText type={'subhead'} style={{ marginBottom: 26 }}>
              <Trans>План подключен и будет действовать до</Trans> <span style={{ fontWeight: 'bold' }}>{activeToLabel}</span>
            </UDText>
            <UDButton style={{ width: 130 }} onClick={closeModal} variant={'primary'}>
              <Trans>Понятно</Trans>
            </UDButton>
          </div>
        ) : (
          <div className={'flex flex-col items-start'}>
            <UDText type={'title-3'} style={{ marginBottom: 30 }}>
              <Trans>{selectedPlanName} план</Trans>
            </UDText>
            <UDText type={'subhead'} style={{ marginBottom: 16 }}>
              <Trans>Подключить этот план:</Trans>
            </UDText>
            <div style={{ marginBottom: 30, display: 'flex' }}>
              <UDButton
                variant="secondary"
                onClick={() => setTariff('month')}
                className={'flex-center'}
                style={{
                  marginRight: 16,
                  border: tariff === 'month' ? `2px solid ${theme.colors.AccentNaplesYellow}` : '2px solid transparent',
                }}
              >
                <Trans>На 1 месяц</Trans>
              </UDButton>
              <UDButton
                variant="secondary"
                disabled
                onClick={() => setTariff('year')}
                className={'flex-center'}
                style={{
                  border: tariff === 'year' ? `2px solid ${theme.colors.AccentNaplesYellow}` : '2px solid transparent',
                  cursor: 'not-allowed',
                }}
              >
                <Trans>На 12 месяцев</Trans>
              </UDButton>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
              <UDButton
                css={disableButtonCSS}
                disabled={!tariff || !isCanBuy}
                style={{ width: 186, height: 36 }}
                onClick={handleGivePlan}
                variant={'primary'}
              >
                <Trans>Подключить</Trans>
              </UDButton>
              <div className="flex flex-col" style={{ gap: 4 }}>
                <UDText type={'caption-1'} style={{ color: theme.colors.DisabledInputColor }}>
                  <Trans>один месяц за {selectedPlanPrice} руб.</Trans>
                </UDText>
                {!isCanBuy && (
                  <UDText type={'caption-1'} style={{ color: theme.colors.DisabledInputColor }}>
                    <Trans>недостаточно средств: {shortage} руб.</Trans>
                  </UDText>
                )}
              </div>
            </div>
          </div>
        )}
      </Modal>
    </MainBlock>
  );
}

export default PlansBlock;
